import {
  ChatBubbleLeftEllipsisIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  ComputerDesktopIcon,
  PauseIcon,
  PlayIcon,
} from "@heroicons/react/24/outline";
import { useCallback, useEffect, useLayoutEffect, useRef, useState } from "react";
import { frameDurationSeconds, terminalStatuses, viewportDimensions } from "@/components/audits/replay/constants";
import type { FrameIssueEmptyCaption } from "@/components/audits/replay/FrameIssueCaption";
import FrameIssueOverlay from "@/components/audits/replay/FrameIssueOverlay";
import { objectContainCursorPosition, stepForFrame, stickyCursorCenter } from "@/components/audits/replay/helpers";
import LiveEvaluationBrowserChrome from "@/components/domains/LiveEvaluationBrowserChrome";
import LiveEvaluationCursor from "@/components/domains/LiveEvaluationCursor";
import LiveEvaluationLiveBanner from "@/components/domains/LiveEvaluationLiveBanner";
import LiveEvaluationPageTabs from "@/components/domains/LiveEvaluationPageTabs";
import LiveEvaluationPlayerImage from "@/components/domains/LiveEvaluationPlayerImage";
import LiveEvaluationReplayControls from "@/components/domains/LiveEvaluationReplayControls";
import MappingSessionOverlay from "@/components/domains/MappingSessionOverlay";
import {
  keepWaitingPaneForFirstLivePaint,
  playbackFrameVisible,
  playerFrameNeedsPaint,
  shouldAdvancePlaybackClock,
  showPlayerLoadingChrome,
} from "@/components/domains/playbackClock";
import { playerPrefetchUrls, prefetchPlayerImages } from "@/components/domains/playerPrefetchUrls";

const PLAYER_FRAME_HEIGHT_CLASS = "min-h-[468px] max-h-[calc(100vh-300px)]";
const DEFAULT_SCREENSHOT_ASPECT = viewportDimensions.desktop.width / viewportDimensions.desktop.height;

import type {
  Finding,
  LiveEvaluation,
  LiveEvaluationInteraction,
  LiveEvaluationPage,
  LiveEvaluationPageKind,
} from "@/types";

function replayEndTime(screenshotCount: number) {
  return Math.max(screenshotCount - 1, 0) * frameDurationSeconds;
}

function frameAtTime(time: number, screenshotCount: number) {
  if (screenshotCount <= 0) {
    return 0;
  }

  return Math.min(Math.floor(time / frameDurationSeconds), screenshotCount - 1);
}

function frameRangeForStepSequences(interaction: LiveEvaluationInteraction, stepSequences: number[]) {
  const sequences = stepSequences.filter((sequence) => sequence > 0).sort((a, b) => a - b);
  if (sequences.length === 0 || interaction.screenshots.length === 0) {
    return { startFrame: 0, endFrame: 0 };
  }

  const screenshotIndexForStep = (step: LiveEvaluationInteraction["steps"][number] | undefined) => {
    if (!step?.screenshot_id) {
      return -1;
    }

    return interaction.screenshots.findIndex((screenshot) => screenshot.id === step.screenshot_id);
  };

  const screenshotIndexAtOrBefore = (sequence: number) => {
    const priorSteps = interaction.steps
      .filter((step) => step.sequence <= sequence)
      .sort((a, b) => b.sequence - a.sequence);

    for (const step of priorSteps) {
      const index = screenshotIndexForStep(step);
      if (index >= 0) {
        return index;
      }
    }

    return 0;
  };

  // Start of the check: min(step_sequences) screenshot, else nearest prior shot.
  const minSequence = sequences[0];
  const startFrame = screenshotIndexAtOrBefore(minSequence);

  // End of the check: first screenshot after max(step_sequences), else max sequence screenshot.
  const maxSequence = sequences[sequences.length - 1];
  const endFrame =
    interaction.steps
      .filter((step) => step.sequence > maxSequence)
      .sort((a, b) => a.sequence - b.sequence)
      .map(screenshotIndexForStep)
      .find((index) => index >= 0) ?? screenshotIndexAtOrBefore(maxSequence);

  return { startFrame, endFrame: Math.max(endFrame, startFrame) };
}

function playheadForStepSequences(interaction: LiveEvaluationInteraction, stepSequences: number[]) {
  return frameRangeForStepSequences(interaction, stepSequences).startFrame * frameDurationSeconds;
}

function playheadForFocus(
  interaction: LiveEvaluationInteraction,
  pending: { screenshotId?: string; stepSequences: number[] },
) {
  if (pending.screenshotId) {
    const index = interaction.screenshots.findIndex((screenshot) => screenshot.id === pending.screenshotId);
    if (index < 0) {
      return null;
    }

    return index * frameDurationSeconds;
  }

  return playheadForStepSequences(interaction, pending.stepSequences);
}

function playheadForFirstFinding(interaction: LiveEvaluationInteraction) {
  const screenshotIds = new Set(
    interaction.findings.flatMap((finding) => (finding.screenshot_id ? [finding.screenshot_id] : [])),
  );
  const index = interaction.screenshots.findIndex((screenshot) => screenshotIds.has(screenshot.id));
  if (index < 0) {
    return replayEndTime(interaction.screenshots.length);
  }

  return index * frameDurationSeconds;
}

function nextFindingFrameIndex(
  screenshots: LiveEvaluationInteraction["screenshots"],
  findingsByScreenshot: Record<string, Finding[]>,
  currentIndex: number,
) {
  const hasFindings = (index: number) => (findingsByScreenshot[screenshots[index]?.id] || []).length > 0;

  for (let index = currentIndex + 1; index < screenshots.length; index += 1) {
    if (hasFindings(index)) {
      return index;
    }
  }

  for (let index = 0; index < currentIndex; index += 1) {
    if (hasFindings(index)) {
      return index;
    }
  }

  return null;
}

function emptyFrameCaption(
  currentCount: number,
  nextIndex: number | null,
  currentIndex: number,
  onSeek: (time: number) => void,
): FrameIssueEmptyCaption | undefined {
  if (currentCount > 0) {
    return undefined;
  }

  if (nextIndex == null) {
    return { message: "No usability issues identified on this page" };
  }

  return {
    message: "No usability issues identified on this page",
    actionLabel: nextIndex > currentIndex ? "Go to next usability issue" : "Go to first usability issue",
    onAction: () => onSeek(nextIndex * frameDurationSeconds),
  };
}

type FocusRequest = {
  pageId?: string;
  interactionId?: string;
  token: number;
  stepSequences?: number[];
  screenshotId?: string;
  findingId?: string;
  viewport?: string;
};

function pageForFocus(pages: LiveEvaluationPage[], focusRequest: FocusRequest | null) {
  if (!focusRequest) {
    return null;
  }

  return (
    (focusRequest.interactionId
      ? pages.find((page) =>
          Object.values(page.interactions ?? {}).some((interaction) => interaction.id === focusRequest.interactionId),
        )
      : null) ||
    pages.find((page) => page.id === focusRequest.pageId) ||
    null
  );
}

type Props = {
  liveEvaluation: LiveEvaluation;
  initialCollapsed?: boolean;
  focusRequest?: FocusRequest | null;
  collapsible?: boolean;
  mappingInProgress?: boolean;
};

export default function LiveEvaluationCard({
  liveEvaluation,
  initialCollapsed = false,
  focusRequest = null,
  collapsible,
  mappingInProgress = false,
}: Props) {
  const { phase, pages, active_page_id: activePageId } = liveEvaluation;
  const complete = phase === "complete";
  const [collapsed, setCollapsed] = useState(initialCollapsed);
  const [prevInitialCollapsed, setPrevInitialCollapsed] = useState(initialCollapsed);
  if (initialCollapsed !== prevInitialCollapsed) {
    setPrevInitialCollapsed(initialCollapsed);
    setCollapsed(initialCollapsed);
  }
  const initialFocusPage = pageForFocus(pages, focusRequest);
  const initialSelectedPageId =
    initialFocusPage?.id ?? focusRequest?.pageId ?? pickInitialPageId(pages, activePageId, mappingInProgress);
  const initialSelectedPage = initialFocusPage || pages.find((page) => page.id === initialSelectedPageId) || null;
  const [selectedPageId, setSelectedPageId] = useState<string | null>(initialSelectedPageId);
  const [selectedViewport, setSelectedViewport] = useState<string | null>(() =>
    focusRequest?.viewport && interactionsForPage(initialSelectedPage)[focusRequest.viewport]
      ? focusRequest.viewport
      : pickInitialViewport(initialSelectedPage),
  );
  const [userPicked, setUserPicked] = useState(Boolean(focusRequest));
  const [currentTime, setCurrentTime] = useState(0);
  const [playing, setPlaying] = useState(false);
  const [playbackHeld, setPlaybackHeld] = useState(false);
  const currentTimeRef = useRef(0);
  const pendingSeekRef = useRef<{
    pageId: string;
    stepSequences: number[];
    screenshotId?: string;
    findingId?: string;
    viewport: string;
    token: number;
  } | null>(null);
  const [highlightFindingId, setHighlightFindingId] = useState<string | null>(focusRequest?.findingId ?? null);
  const playbackEndTimeRef = useRef<number | null>(null);
  const wasLiveFollowRef = useRef(false);
  const cardRef = useRef<HTMLElement | null>(null);
  // Guest reports seed a focus on first paint. Treat that token as already scrolled so load
  // does not animate the player into view. Later Watch clicks still scroll.
  const scrolledFocusTokenRef = useRef<number | null>(focusRequest?.token ?? null);
  const appliedFocusTokenRef = useRef<number | null>(null);

  const setPlayhead = useCallback((time: number) => {
    currentTimeRef.current = time;
    setCurrentTime(time);
  }, []);

  // Token identifies a focus request; depending on the whole object would re-run on referential churn.
  // biome-ignore lint/correctness/useExhaustiveDependencies: focusRequest.token is the request identity
  useEffect(() => {
    if (!focusRequest) {
      return;
    }

    const focusPage = pageForFocus(pages, focusRequest);
    const focusPageId = focusPage?.id ?? focusRequest.pageId;
    if (!focusPageId) {
      return;
    }
    if (appliedFocusTokenRef.current === focusRequest.token) {
      return;
    }

    appliedFocusTokenRef.current = focusRequest.token;

    const focusViewport =
      focusRequest.viewport && interactionsForPage(focusPage)[focusRequest.viewport]
        ? focusRequest.viewport
        : pickInitialViewport(focusPage);

    pendingSeekRef.current = {
      pageId: focusPageId,
      stepSequences: focusRequest.stepSequences ?? [],
      screenshotId: focusRequest.screenshotId,
      findingId: focusRequest.findingId,
      viewport: focusViewport || "desktop",
      token: focusRequest.token,
    };
    playbackEndTimeRef.current = null;
    setHighlightFindingId(focusRequest.findingId ?? null);
    setCollapsed(false);
    setUserPicked(true);
    setSelectedPageId(focusPageId);
    setSelectedViewport(focusViewport);
    setPlaying(false);
  }, [focusRequest?.token, pages]);

  // Scroll after expand: collapsed card is header-only, so measuring it would stop short of the player.
  // biome-ignore lint/correctness/useExhaustiveDependencies: focusRequest.token is the request identity
  useLayoutEffect(() => {
    if (!focusRequest || collapsed) {
      return;
    }
    if (scrolledFocusTokenRef.current === focusRequest.token) {
      return;
    }

    scrolledFocusTokenRef.current = focusRequest.token;
    cardRef.current?.scrollIntoView({ behavior: "smooth", block: "start" });
  }, [focusRequest?.token, collapsed]);

  useEffect(() => {
    if (userPicked) {
      return;
    }

    setSelectedPageId(pickInitialPageId(pages, activePageId, mappingInProgress));
  }, [pages, activePageId, mappingInProgress, userPicked]);

  useEffect(() => {
    if (userPicked) {
      return;
    }

    setSelectedViewport(pickInitialViewport(pages.find((page) => page.id === selectedPageId) || null));
  }, [pages, selectedPageId, userPicked]);

  const selectedPage =
    pages.find((page) => page.id === selectedPageId) ||
    pages.find((page) => page.id === pickInitialPageId(pages, activePageId, mappingInProgress)) ||
    null;
  const pageInteractions = interactionsForPage(selectedPage);
  const availableViewports = orderedViewports(Object.keys(pageInteractions));
  const activeViewport =
    selectedViewport && pageInteractions[selectedViewport] ? selectedViewport : pickInitialViewport(selectedPage);
  const interaction = activeViewport ? pageInteractions[activeViewport] : null;
  const terminal = interaction ? terminalStatuses.includes(interaction.status) : false;
  const togglePlayback = () => {
    if (!interaction || interaction.screenshots.length === 0) {
      return;
    }

    if (playing) {
      setPlaying(false);
      return;
    }

    const totalDuration = replayEndTime(interaction.screenshots.length);
    if (currentTimeRef.current >= totalDuration) {
      playbackEndTimeRef.current = null;
      setPlayhead(0);
      setPlaying(interaction.screenshots.length > 1);
      return;
    }

    setPlaying(true);
  };
  // Follow latest frames for whichever tab is selected while that interaction is still
  // running — not only the server "active" page. Parallel page evals update every tab's
  // payload on DomainChannel reload; gating on activePageId left other tabs stuck on frame 0.
  const liveFollow = Boolean(interaction) && interaction?.status === "running";
  const currentFrame = frameAtTime(currentTime, interaction?.screenshots.length || 0);

  // Narrow deps: full `interaction` would reset the playhead on every live screenshot append.
  // biome-ignore lint/correctness/useExhaustiveDependencies: interaction.id is the tab identity
  useEffect(() => {
    setPlaying(false);
    if (!interaction) {
      setPlayhead(0);
      return;
    }

    const pending = pendingSeekRef.current;
    if (pending) {
      // Wait until selected page matches — applying on the prior interaction wipes cross-page Watch.
      if (selectedPageId !== pending.pageId) {
        return;
      }
      if (activeViewport !== pending.viewport) {
        return;
      }

      pendingSeekRef.current = null;
      const playhead = playheadForFocus(interaction, pending);
      if (playhead != null) {
        setPlayhead(playhead);

        const checklistFocus =
          pending.stepSequences.length > 0 && !pending.screenshotId && terminalStatuses.includes(interaction.status);
        if (checklistFocus) {
          const range = frameRangeForStepSequences(interaction, pending.stepSequences);
          playbackEndTimeRef.current = range.endFrame * frameDurationSeconds;
          if (range.endFrame > range.startFrame) {
            setPlaying(true);
          }
        } else if (
          !pending.screenshotId &&
          pending.stepSequences.length === 0 &&
          interaction.status !== "running" &&
          interaction.screenshots.length > 1
        ) {
          // Page-level Watch (Coverage status / handoff): start replay from the focus playhead.
          playbackEndTimeRef.current = null;
          setPlaying(true);
        }
      }
      return;
    }

    // Tab switch / open player: first finding frame so the page's issues are visible.
    // Running evals stay on the latest screenshot. Watch uses pendingSeek above.
    playbackEndTimeRef.current = null;
    if (interaction.status === "running") {
      setPlayhead(replayEndTime(interaction.screenshots.length));
      return;
    }

    setPlayhead(playheadForFirstFinding(interaction));
  }, [interaction?.id, selectedPageId, activeViewport, focusRequest?.token, setPlayhead]);

  useEffect(() => {
    if (!interaction) {
      wasLiveFollowRef.current = false;
      return;
    }

    // Stick to newest frame while running.
    if (liveFollow) {
      wasLiveFollowRef.current = true;
      setPlayhead(replayEndTime(interaction.screenshots.length));
      return;
    }

    // Final reload often ships last screenshot + terminal status together. If we
    // only followed while running, playhead stays at the previous end → 2nd-last.
    if (wasLiveFollowRef.current) {
      wasLiveFollowRef.current = false;
      setPlayhead(replayEndTime(interaction.screenshots.length));
    }
  }, [liveFollow, interaction, interaction?.screenshots.length, setPlayhead]);

  // Autoplay spends frameDurationSeconds on a frame only after that screenshot has painted.
  // biome-ignore lint/correctness/useExhaustiveDependencies: interaction id/length drive the clock; full object churns every frame
  useEffect(() => {
    if (!shouldAdvancePlaybackClock(playing, !playbackHeld) || !interaction) {
      return;
    }

    const totalDuration = replayEndTime(interaction.screenshots.length);
    const playbackEndTime = Math.min(playbackEndTimeRef.current ?? totalDuration, totalDuration);
    if (totalDuration <= 0 || currentTimeRef.current >= playbackEndTime) {
      setPlaying(false);
      return;
    }

    let last = performance.now();
    let rafId = 0;

    const tick = (now: number) => {
      const next = Math.min(currentTimeRef.current + (now - last) / 1000, playbackEndTime);
      last = now;
      setPlayhead(next);
      if (next >= playbackEndTime) {
        playbackEndTimeRef.current = null;
        setPlaying(false);
        return;
      }

      rafId = window.requestAnimationFrame(tick);
    };

    rafId = window.requestAnimationFrame(tick);
    return () => window.cancelAnimationFrame(rafId);
  }, [playing, playbackHeld, interaction?.id, interaction?.screenshots.length, setPlayhead]);

  const badge = badgeForPhase(phase);
  const atLatestFrame = !interaction?.screenshots.length || currentFrame >= interaction.screenshots.length - 1;
  // Terminal + last frame: replace open-ended last intent with wrap-up (scrub keeps real thoughts).
  const browseWrapUp = Boolean(terminal && interaction?.screenshots.length && atLatestFrame);
  const thought = browseWrapUp
    ? pageKind(selectedPage) === "journey_map"
      ? "We've got what we need from this mapping pass."
      : "We've got what we need from this page."
    : thoughtForSelection({
        phase,
        selectedPage,
        interaction,
        liveFollow,
        currentFrame,
      });
  const actionLabel =
    browseWrapUp || !interaction
      ? null
      : (liveFollow ? latestStep(interaction) : frameStep(interaction, currentFrame))?.title;

  const canToggleCollapse = collapsible ?? complete;
  const title = "Agent sessions";
  const subtitle = mappingInProgress
    ? "Watch the agent sign in and map signed-in journeys."
    : complete
      ? "Watch how we interacted with your product to evaluate its usability."
      : "The agent is using your site like a visitor, looking for UX issues.";

  return (
    <section ref={cardRef} className="dashboard-section scroll-mt-4 overflow-hidden">
      <button
        type="button"
        className={`flex w-full items-center justify-between gap-3 border-b border-gray-100 bg-white px-5 py-4 text-left sm:px-6 ${
          canToggleCollapse ? "cursor-pointer hover:bg-gray-50" : "cursor-default"
        }`}
        onClick={() => {
          if (!canToggleCollapse) {
            return;
          }

          if (collapsed) {
            const mappingPage = mappingInProgress ? pages.find((page) => pageKind(page) === "journey_map") : null;
            if (mappingPage) {
              setSelectedPageId(mappingPage.id);
              setSelectedViewport(pickInitialViewport(mappingPage));
              setPlaying(false);
              setHighlightFindingId(null);
            } else if (interaction && !pendingSeekRef.current && interaction.status !== "running") {
              playbackEndTimeRef.current = null;
              setPlayhead(playheadForFirstFinding(interaction));
              setPlaying(false);
            }
          }

          setCollapsed((value) => !value);
        }}
        aria-expanded={!collapsed}
      >
        <div className="flex min-w-0 items-center gap-4">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-primary/10 text-primary">
            <ComputerDesktopIcon className="h-6 w-6" aria-hidden="true" />
          </span>
          <div className="min-w-0">
            <p className="text-lg font-semibold text-gray-900 sm:text-xl">{title}</p>
            <p className="text-sm text-gray-500 sm:text-base">{subtitle}</p>
          </div>
        </div>
        <div className="flex shrink-0 items-center gap-3">
          {badge ? (
            <span className="inline-flex items-center gap-2 rounded-md bg-primary px-3 py-1 text-sm text-white">
              <span className="h-2 w-2 animate-pulse rounded-full bg-white" />
              {badge}
            </span>
          ) : null}
          {canToggleCollapse ? (
            <span className="text-gray-500">
              {collapsed ? (
                <ChevronDownIcon className="h-6 w-6" aria-hidden="true" />
              ) : (
                <ChevronUpIcon className="h-6 w-6" aria-hidden="true" />
              )}
            </span>
          ) : null}
        </div>
      </button>

      {!collapsed ? (
        <>
          <LiveEvaluationPageTabs
            pages={pages}
            selectedPageId={selectedPage?.id || null}
            activePageId={activePageId}
            mappingInProgress={mappingInProgress}
            availableViewports={availableViewports}
            activeViewport={activeViewport}
            onSelect={(pageId) => {
              setUserPicked(true);
              setSelectedPageId(pageId);
              setSelectedViewport(pickInitialViewport(pages.find((page) => page.id === pageId) || null));
              setPlaying(false);
              setHighlightFindingId(null);
            }}
            onSelectViewport={(viewport) => {
              if (viewport === activeViewport) {
                return;
              }

              setUserPicked(true);
              setPlaying(false);
              playbackEndTimeRef.current = null;

              const currentScreenshotId =
                interaction?.screenshots[frameAtTime(currentTimeRef.current, interaction.screenshots.length)]?.id;
              const match = findingToFollowOnViewportSwitch(
                interaction,
                pageInteractions[viewport],
                highlightFindingId,
                currentScreenshotId,
              );

              if (match?.screenshot_id && selectedPage?.id) {
                pendingSeekRef.current = {
                  pageId: selectedPage.id,
                  stepSequences: [],
                  screenshotId: match.screenshot_id,
                  findingId: match.id,
                  viewport,
                  token: Date.now(),
                };
                setHighlightFindingId(match.id);
              } else {
                setHighlightFindingId(null);
              }

              setSelectedViewport(viewport);
            }}
          />
          <PlayerBody
            key={interaction?.id ?? "empty"}
            phase={phase}
            pageKind={pageKind(selectedPage)}
            interaction={interaction}
            siblingInteractions={Object.values(pageInteractions).filter((item) => item.id !== interaction?.id)}
            currentTime={currentTime}
            liveFollow={liveFollow}
            terminal={terminal}
            playing={playing}
            focusToken={focusRequest?.token ?? null}
            highlightFindingId={highlightFindingId}
            onOpenFinding={(findingId) => setHighlightFindingId(findingId)}
            onSeek={(time, options) => {
              playbackEndTimeRef.current = null;
              setPlayhead(time);
              setPlaying(options?.play ?? false);
              setHighlightFindingId(null);
            }}
            onTogglePlayback={togglePlayback}
            onPlaybackHoldChange={setPlaybackHeld}
          />
          <div className="bg-white px-5 pb-4 sm:px-6">
            <div className="flex gap-3 rounded-lg bg-gray-50 px-3 py-4 sm:px-4">
              <ChatBubbleLeftEllipsisIcon className="mt-0.5 h-5 w-5 shrink-0 text-primary" aria-hidden="true" />
              <div className="min-w-0">
                {actionLabel ? (
                  <>
                    <p className="text-sm leading-relaxed text-gray-800 sm:text-base">{actionLabel}</p>
                    <p className="mt-1.5 truncate text-xs text-gray-500">{thought}</p>
                  </>
                ) : (
                  <p className="truncate text-sm leading-relaxed text-gray-800 sm:text-base">{thought}</p>
                )}
              </div>
            </div>
          </div>
        </>
      ) : null}
    </section>
  );
}

function PlayerBody({
  phase,
  pageKind,
  interaction,
  siblingInteractions,
  currentTime,
  liveFollow,
  terminal,
  playing,
  focusToken,
  highlightFindingId,
  onOpenFinding,
  onSeek,
  onTogglePlayback,
  onPlaybackHoldChange,
}: {
  phase: LiveEvaluation["phase"];
  pageKind: LiveEvaluationPageKind;
  interaction: LiveEvaluationInteraction | null;
  siblingInteractions: LiveEvaluationInteraction[];
  currentTime: number;
  liveFollow: boolean;
  terminal: boolean;
  playing: boolean;
  focusToken: number | null;
  highlightFindingId: string | null;
  onOpenFinding: (findingId: string) => void;
  onSeek: (time: number, options?: { play?: boolean }) => void;
  onTogglePlayback: () => void;
  onPlaybackHoldChange: (held: boolean) => void;
}) {
  const playerRef = useRef<HTMLDivElement | null>(null);
  const playerVisibleRef = useRef(false);
  const [frameSize, setFrameSize] = useState({ width: 0, height: 0 });
  const [imageAspect, setImageAspect] = useState(DEFAULT_SCREENSHOT_ASPECT);
  const [displayedFrame, setDisplayedFrame] = useState<{ id: string; url: string } | null>(null);
  const frameIndex =
    interaction && interaction.screenshots.length > 0
      ? liveFollow
        ? Math.max(interaction.screenshots.length - 1, 0)
        : frameAtTime(currentTime, interaction.screenshots.length)
      : 0;
  const pendingScreenshot = interaction?.screenshots[frameIndex] ?? null;
  const pendingId = pendingScreenshot?.id ?? null;
  const pendingImageUrl = pendingScreenshot?.image_url ?? null;
  const frameVisible = playbackFrameVisible(displayedFrame?.id ?? null, pendingId);
  const imageLoading = !frameVisible;
  const showLoadingChrome = showPlayerLoadingChrome(liveFollow, frameVisible);
  const waitingForFirstLivePaint = keepWaitingPaneForFirstLivePaint(liveFollow, displayedFrame?.id ?? null, pendingId);
  const playerMounted = Boolean(interaction?.screenshots.length) && !waitingForFirstLivePaint;

  useEffect(() => {
    if (!interaction?.screenshots.length) {
      return;
    }

    prefetchPlayerImages(playerPrefetchUrls(interaction, frameIndex, siblingInteractions));
  }, [frameIndex, interaction, siblingInteractions]);

  useEffect(() => {
    onPlaybackHoldChange(imageLoading);
  }, [imageLoading, onPlaybackHoldChange]);

  useEffect(() => {
    return () => onPlaybackHoldChange(false);
  }, [onPlaybackHoldChange]);

  useEffect(() => {
    if (!playerFrameNeedsPaint(displayedFrame, pendingId, pendingImageUrl) || !pendingId || !pendingImageUrl) {
      return;
    }

    let cancelled = false;
    const image = new Image();
    const commit = () => {
      if (cancelled || image.naturalWidth <= 0) {
        return;
      }

      setImageAspect(image.naturalWidth / image.naturalHeight);
      setDisplayedFrame({ id: pendingId, url: pendingImageUrl });
    };

    image.onload = commit;
    image.src = pendingImageUrl;
    if (image.complete) {
      commit();
    }

    return () => {
      cancelled = true;
    };
  }, [displayedFrame, pendingId, pendingImageUrl]);

  useEffect(() => {
    if (focusToken == null || !terminal) {
      return;
    }

    playerRef.current?.focus({ preventScroll: true });
  }, [focusToken, terminal, interaction?.id]);

  useEffect(() => {
    playerVisibleRef.current = false;
    const player = playerRef.current;
    if (!terminal || !player) {
      return;
    }

    const observer = new IntersectionObserver(
      ([entry]) => {
        playerVisibleRef.current = entry.isIntersecting;
      },
      { threshold: 0.25 },
    );
    observer.observe(player);

    return () => {
      observer.disconnect();
      playerVisibleRef.current = false;
    };
  }, [terminal, interaction?.id]);

  useLayoutEffect(() => {
    const player = playerRef.current;
    if (!player || !playerMounted) {
      setFrameSize({ width: 0, height: 0 });
      return;
    }

    const syncSize = () => {
      const { width, height } = player.getBoundingClientRect();
      setFrameSize({ width, height });
    };
    syncSize();

    const observer = new ResizeObserver(([entry]) => {
      const { width, height } = entry.contentRect;
      setFrameSize({ width, height });
    });
    observer.observe(player);
    return () => observer.disconnect();
  }, [playerMounted]);

  useEffect(() => {
    if (!terminal) {
      return;
    }

    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.code !== "Space" || event.defaultPrevented || !playerVisibleRef.current) {
        return;
      }

      const activeElement = document.activeElement;
      const focusedInteractiveElement =
        activeElement instanceof HTMLButtonElement ||
        activeElement instanceof HTMLAnchorElement ||
        activeElement instanceof HTMLInputElement ||
        activeElement instanceof HTMLSelectElement ||
        activeElement instanceof HTMLTextAreaElement ||
        (activeElement instanceof HTMLElement && activeElement.isContentEditable);

      if (focusedInteractiveElement) {
        return;
      }

      event.preventDefault();
      if (!event.repeat) {
        onTogglePlayback();
      }
    };

    window.addEventListener("keydown", handleKeyDown);
    return () => window.removeEventListener("keydown", handleKeyDown);
  }, [onTogglePlayback, terminal]);

  if (!interaction || interaction.screenshots.length === 0 || waitingForFirstLivePaint) {
    if (pageKind === "journey_map") {
      return (
        <WaitingPane
          chromeUrl={phase === "complete" ? "Mapping session…" : "Signing in…"}
          chromeTitle="Mapping journeys"
          title={phase === "complete" ? "No frames from mapping" : "Mapping journeys"}
          body={
            phase === "complete"
              ? "This mapping pass didn’t capture frames we can replay."
              : "We’re signing in as the test user and looking for the main jobs in the product. This isn’t a usability audit."
          }
          mode={phase === "complete" ? "warming" : "advancing"}
        />
      );
    }

    if (phase === "analyzing") {
      return (
        <WaitingPane
          chromeUrl="Reviewing…"
          chromeTitle="Usability review"
          title="Reviewing for UX issues"
          body="We’re analyzing the pages we browsed. Open issues below will keep filling in."
          mode="analyzing"
        />
      );
    }

    if (phase === "advancing") {
      return (
        <WaitingPane
          chromeUrl="Connecting…"
          chromeTitle="Starting"
          title="Warming up"
          body="We’re opening this page and getting ready to browse like a real visitor."
          mode="advancing"
        />
      );
    }

    if (phase === "complete") {
      if (interaction?.status === "failed") {
        return (
          <WaitingPane
            chromeUrl="Browse failed…"
            chromeTitle="Failed"
            title="Browse failed"
            body={interaction.finish_reason?.trim() || "Browse failed."}
            mode="failed"
          />
        );
      }

      return (
        <WaitingPane
          chromeUrl="No frames…"
          chromeTitle="Empty"
          title="No screenshots for this page"
          body="This page’s latest browse didn’t capture frames we can replay."
          mode="warming"
        />
      );
    }

    return (
      <WaitingPane
        chromeUrl="Connecting…"
        chromeTitle="Starting"
        title="Warming up"
        body="We’re opening your site and getting ready to browse like a real visitor."
        mode="warming"
      />
    );
  }

  const screenshot = interaction.screenshots[frameIndex];
  const displayedScreenshot = interaction.screenshots.find((frame) => frame.id === displayedFrame?.id) ?? null;
  const displayedIndex = displayedScreenshot
    ? interaction.screenshots.findIndex((frame) => frame.id === displayedScreenshot.id)
    : frameIndex;
  const overlayScreenshot = displayedScreenshot;
  const overlayIndex = displayedScreenshot ? displayedIndex : frameIndex;
  const step = overlayScreenshot ? frameStep(interaction, overlayIndex) : null;
  const cursorCenter = overlayScreenshot
    ? stickyCursorCenter(interaction.steps, interaction.screenshots, overlayIndex)
    : null;
  const cursor = cursorCenter
    ? objectContainCursorPosition({
        containerWidth: frameSize.width,
        containerHeight: frameSize.height,
        imageAspect,
        x: cursorCenter.x,
        y: cursorCenter.y,
      })
    : null;
  const showCursorPing =
    Boolean(overlayScreenshot) &&
    (liveFollow || playing) &&
    (step?.action_type === "click" || step?.action_type === "fill");
  const pageUrl = overlayScreenshot?.url || screenshot?.url || "…";
  const pageTitle = overlayScreenshot?.title || screenshot?.title || "Browsing";
  const playbackTime = liveFollow ? replayEndTime(interaction.screenshots.length) : currentTime;
  const findingsByScreenshot = interaction.findings.reduce<Record<string, Finding[]>>((grouped, finding) => {
    if (!finding.screenshot_id) return grouped;

    grouped[finding.screenshot_id] = [...(grouped[finding.screenshot_id] || []), finding];
    return grouped;
  }, {});
  const currentFrameFindings = overlayScreenshot ? findingsByScreenshot[overlayScreenshot.id] || [] : [];
  const findingMarks = interaction.screenshots.map((frame) => ({
    id: frame.id,
    count: (findingsByScreenshot[frame.id] || []).length,
  }));
  const nextIssueFrame = nextFindingFrameIndex(interaction.screenshots, findingsByScreenshot, overlayIndex);
  const mappingSession = pageKind === "journey_map";
  const emptyCaption =
    mappingSession || showLoadingChrome
      ? undefined
      : emptyFrameCaption(currentFrameFindings.length, nextIssueFrame, overlayIndex, onSeek);

  return (
    <div className="px-3 py-3 sm:px-5 sm:pb-5">
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <LiveEvaluationBrowserChrome url={pageUrl} title={pageTitle} />
        {/* Custom replay surface: Space toggles play when focused; not a native control. */}
        {/* biome-ignore lint/a11y/noStaticElementInteractions: keyboard play/pause for the replay frame */}
        {/* biome-ignore lint/a11y/useAriaPropsSupportedByRole: application role owns the Space binding */}
        <div
          ref={playerRef}
          className={`group relative aspect-[1440/1024] w-full overflow-hidden ${PLAYER_FRAME_HEIGHT_CLASS} bg-slate-50 ${
            terminal
              ? "focus:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary"
              : ""
          }`}
          tabIndex={terminal ? 0 : undefined}
          role={terminal ? "application" : undefined}
          aria-label={terminal ? "Interaction replay. Press Space to play or pause." : undefined}
          aria-busy={showLoadingChrome || undefined}
          onKeyDown={(event) => {
            if (event.target !== event.currentTarget || event.key !== " ") {
              return;
            }

            event.preventDefault();
            onTogglePlayback();
          }}
        >
          {displayedFrame?.url ? (
            <LiveEvaluationPlayerImage
              src={displayedFrame.url}
              alt={displayedScreenshot?.title || pageTitle}
              dimmed={showLoadingChrome}
            />
          ) : null}
          {showLoadingChrome ? (
            <div className="pointer-events-none absolute inset-0 z-10 flex items-center justify-center">
              <div
                className="h-10 w-10 animate-spin rounded-full border-2 border-white/80 border-t-primary shadow-sm"
                aria-hidden="true"
              />
            </div>
          ) : null}
          {cursor ? (
            <LiveEvaluationCursor x={cursor.x} y={cursor.y} viewport={interaction.viewport} ping={showCursorPing} />
          ) : null}
          {mappingSession ? <MappingSessionOverlay live={!terminal} /> : null}
          {overlayScreenshot &&
          !mappingSession &&
          (currentFrameFindings.length > 0 || (phase === "complete" && !showLoadingChrome)) ? (
            <FrameIssueOverlay
              findings={currentFrameFindings}
              frameId={overlayScreenshot.id}
              frameSize={frameSize}
              imageAspect={imageAspect}
              highlightFindingId={highlightFindingId}
              onOpenFinding={onOpenFinding}
              emptyCaption={emptyCaption}
            />
          ) : null}
          {terminal ? (
            <button
              type="button"
              className={`absolute left-1/2 top-1/2 z-20 flex h-16 w-16 -translate-x-1/2 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full bg-black/60 text-white shadow-lg backdrop-blur-sm transition-all hover:scale-105 hover:bg-black/75 hover:opacity-100 focus-visible:opacity-100 focus:outline-none focus-visible:ring-2 focus-visible:ring-white ${
                playing
                  ? "opacity-0 group-hover:opacity-20 group-focus-within:opacity-20"
                  : "opacity-0 group-hover:opacity-100 group-focus-within:opacity-100"
              }`}
              onClick={onTogglePlayback}
              aria-label={playing ? "Pause replay" : "Play replay"}
            >
              {playing ? (
                <PauseIcon className="h-8 w-8" aria-hidden="true" />
              ) : (
                <PlayIcon className="ml-1 h-8 w-8" aria-hidden="true" />
              )}
            </button>
          ) : null}
        </div>
        {terminal ? (
          <LiveEvaluationReplayControls
            screenshotCount={interaction.screenshots.length}
            currentTime={playbackTime}
            playing={playing}
            findingMarks={findingMarks}
            onSeek={onSeek}
            onTogglePlayback={onTogglePlayback}
          />
        ) : liveFollow ? (
          <LiveEvaluationLiveBanner />
        ) : null}
      </div>
    </div>
  );
}

function WaitingPane({
  chromeUrl,
  chromeTitle,
  title,
  body,
  mode,
}: {
  chromeUrl: string;
  chromeTitle: string;
  title: string;
  body: string;
  mode: "warming" | "advancing" | "analyzing" | "failed";
}) {
  const failed = mode === "failed";

  return (
    <div className="px-3 py-3 sm:px-5 sm:pb-5">
      <div className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm">
        <LiveEvaluationBrowserChrome url={chromeUrl} title={chromeTitle} />
        <div
          className={`relative aspect-[1440/1024] w-full ${PLAYER_FRAME_HEIGHT_CLASS} overflow-hidden bg-gradient-to-b from-slate-50 via-white to-slate-100`}
        >
          <div className="pointer-events-none absolute inset-0 opacity-40">
            <div
              className={`absolute -left-10 top-8 h-40 w-40 rounded-full blur-3xl ${
                failed ? "bg-error-light/60" : "bg-primary/20"
              }`}
            />
            <div
              className={`absolute bottom-6 right-0 h-48 w-48 rounded-full blur-3xl ${
                failed ? "bg-error-light/40" : "bg-primary/10"
              }`}
            />
          </div>

          {!failed ? (
            <div className="absolute inset-x-0 top-0 border-b border-gray-200/80 bg-white/80 px-6 py-4 backdrop-blur-sm">
              <div className="flex items-center justify-between gap-4">
                <div className="h-4 w-24 animate-pulse rounded bg-gray-200" />
                <div className="flex gap-3">
                  <div className="h-3 w-12 animate-pulse rounded bg-gray-200" />
                  <div className="h-3 w-14 animate-pulse rounded bg-primary/30" />
                  <div className="h-3 w-12 animate-pulse rounded bg-gray-200" />
                </div>
              </div>
            </div>
          ) : null}

          <div className="absolute inset-0 flex flex-col items-center justify-center gap-4 px-6 text-center">
            {failed ? (
              <div className="flex h-14 w-14 items-center justify-center rounded-full bg-error-light text-xl font-semibold text-error">
                !
              </div>
            ) : (
              <div className="relative">
                <div className="h-14 w-14 animate-spin rounded-full border-[3px] border-gray-200 border-t-primary" />
                <div className="absolute inset-0 m-auto h-6 w-6 animate-pulse rounded-full bg-primary/20" />
              </div>
            )}
            <div className="max-w-sm">
              <p className={`text-base font-semibold ${failed ? "text-error-dark" : "text-gray-900"}`}>{title}</p>
              <p className="mt-1.5 text-sm leading-relaxed text-gray-500">{body}</p>
            </div>
            {!failed ? (
              <div className="mt-2 flex items-center gap-2">
                <PulseDot delay="0ms" />
                <PulseDot delay="150ms" />
                <PulseDot delay="300ms" />
              </div>
            ) : null}
            {mode === "analyzing" ? (
              <div className="mt-2 grid w-full max-w-md grid-cols-3 gap-2">
                <div className="h-16 animate-pulse rounded-lg border border-gray-200 bg-white/80" />
                <div className="h-16 animate-pulse rounded-lg border border-primary/20 bg-primary/5" />
                <div className="h-16 animate-pulse rounded-lg border border-gray-200 bg-white/80" />
              </div>
            ) : null}
          </div>
        </div>
      </div>
    </div>
  );
}

function PulseDot({ delay }: { delay: string }) {
  return <span className="h-2 w-2 animate-pulse rounded-full bg-primary" style={{ animationDelay: delay }} />;
}

function pickInitialPageId(pages: LiveEvaluationPage[], activePageId: string | null, preferMapping = false) {
  if (preferMapping) {
    const mappingPage = pages.find((page) => pageKind(page) === "journey_map");
    if (mappingPage) {
      return mappingPage.id;
    }
  }

  const evaluationPages = pages.filter((page) => pageKind(page) !== "journey_map");
  const pool = evaluationPages.length > 0 ? evaluationPages : pages;

  if (activePageId && pool.some((page) => page.id === activePageId)) {
    return activePageId;
  }

  const started = pool.find((page) => page.started);
  return started?.id || pool[0]?.id || null;
}

function pageKind(page: LiveEvaluationPage | null): LiveEvaluationPageKind {
  return page?.kind ?? "page";
}

function interactionsForPage(page: LiveEvaluationPage | null): Record<string, LiveEvaluationInteraction> {
  if (!page) {
    return {};
  }

  return page.interactions ?? {};
}

function pickInitialViewport(page: LiveEvaluationPage | null) {
  const available = interactionsForPage(page);
  return available.desktop ? "desktop" : Object.keys(available)[0] || null;
}

function findingToFollowOnViewportSwitch(
  sourceInteraction: LiveEvaluationInteraction | null,
  targetInteraction: LiveEvaluationInteraction | undefined,
  highlightFindingId: string | null,
  currentScreenshotId: string | undefined,
) {
  if (!sourceInteraction || !targetInteraction) {
    return null;
  }

  const highlighted = highlightFindingId
    ? sourceInteraction.findings.find((finding) => finding.id === highlightFindingId)
    : undefined;
  const candidates = highlighted
    ? [highlighted]
    : sourceInteraction.findings
        .filter((finding) => finding.screenshot_id === currentScreenshotId)
        .sort((first, second) => (second.severity || 0) - (first.severity || 0));

  for (const finding of candidates) {
    if (!finding.domain_issue_id) {
      continue;
    }

    const match = targetInteraction.findings.find(
      (other) => other.domain_issue_id === finding.domain_issue_id && other.screenshot_id,
    );
    if (match) {
      return match;
    }
  }

  return null;
}

function orderedViewports(viewports: string[]) {
  const unique = [...new Set(viewports)];
  return ["desktop", "mobile"]
    .filter((viewport) => unique.includes(viewport))
    .concat(unique.filter((viewport) => !["desktop", "mobile"].includes(viewport)));
}

function badgeForPhase(phase: LiveEvaluation["phase"]) {
  switch (phase) {
    case "analyzing":
      return "Analyzing";
    case "complete":
      return null;
    default:
      return "In progress";
  }
}

function thoughtForSelection({
  phase,
  selectedPage,
  interaction,
  liveFollow,
  currentFrame,
}: {
  phase: LiveEvaluation["phase"];
  selectedPage: LiveEvaluationPage | null;
  interaction: LiveEvaluationInteraction | null;
  liveFollow: boolean;
  currentFrame: number;
}) {
  const mapping = pageKind(selectedPage) === "journey_map";
  if (interaction?.screenshots.length) {
    if (liveFollow) {
      return (
        latestStep(interaction)?.reasoning ||
        (mapping
          ? "Signing in and looking for the main jobs in the product."
          : "Browsing the page like a real visitor.")
      );
    }

    return (
      frameStep(interaction, currentFrame)?.reasoning ||
      (mapping ? "Signing in and looking for the main jobs in the product." : "Browsing the page like a real visitor.")
    );
  }

  if (mapping) {
    if (phase === "complete") {
      return selectedPage
        ? `Latest mapping session for ${selectedPage.title?.trim() || selectedPage.path}.`
        : "Replay how we mapped signed-in journeys.";
    }

    return "We’re signing in as the test user to map journeys.";
  }

  if (phase === "analyzing") {
    return "Browsing is done. We’re reviewing what we saw for usability issues—findings still show up in Open issues below.";
  }

  if (phase === "advancing") {
    return "Warming up to interact with your website…";
  }

  if (phase === "complete") {
    if (interaction?.status === "failed") {
      return interaction.finish_reason?.trim() || "Browse failed.";
    }

    return selectedPage
      ? `Latest browse for ${selectedPage.title?.trim() || selectedPage.path}.`
      : "Replay how we browsed your pages.";
  }

  return "Warming up to interact with your website…";
}

function latestStep(interaction: LiveEvaluationInteraction) {
  return stepForFrame(interaction.steps, interaction.screenshots, interaction.screenshots.length - 1) ?? null;
}

function frameStep(interaction: LiveEvaluationInteraction, frameIndex: number) {
  return stepForFrame(interaction.steps, interaction.screenshots, frameIndex) ?? null;
}
